import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Gift, ShoppingBag, X } from 'lucide-react';
import { useSettings } from '@/hooks/useSettings';
import { useProducts } from '@/hooks/useProducts';
import { useCartStore } from '@/store/cartStore';
import { Link } from 'react-router-dom';

const POPUP_DISMISSED_KEY = 'promo_popup_dismissed';
const POPUP_DISMISSED_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

const PromoPopup = () => {
  const [open, setOpen] = useState(false);
  const { data: settings } = useSettings();
  const { data: products = [] } = useProducts();
  const cartTotal = useCartStore((state) => state.getTotalPrice());

  const popupEnabled = settings?.popup_enabled;
  const popupProductId = settings?.popup_product_id;
  const popupText = settings?.popup_text || '';
  const popupMinCartValue = settings?.popup_min_cart_value || 0;

  const product = products.find(p => p.id === popupProductId);

  useEffect(() => {
    if (!popupEnabled || !popupProductId) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem(POPUP_DISMISSED_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed);
      if (Date.now() - dismissedAt < POPUP_DISMISSED_EXPIRY) return;
    }

    // Show after a short delay
    const timer = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(timer);
  }, [popupEnabled, popupProductId]);

  const handleClose = () => {
    setOpen(false);
    localStorage.setItem(POPUP_DISMISSED_KEY, Date.now().toString());
  };

  if (!popupEnabled || !product) return null;

  const meetsMinCart = popupMinCartValue > 0 ? cartTotal >= popupMinCartValue : true;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-sm mx-auto rounded-2xl p-0 overflow-hidden border-0" dir="rtl">
        <DialogTitle className="sr-only">عرض خاص</DialogTitle>
        
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary/80 p-4 text-center relative">
          <button onClick={handleClose} className="absolute top-2 left-2 text-primary-foreground/70 hover:text-primary-foreground">
            <X className="w-5 h-5" />
          </button>
          <Gift className="w-10 h-10 text-primary-foreground mx-auto mb-2" />
          <h2 className="text-xl font-bold text-primary-foreground">🎁 عرض خاص!</h2>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Custom text */}
          {popupText && (
            <p className="text-center text-foreground font-medium text-base leading-relaxed">
              {popupText}
            </p>
          )}

          {/* Product card */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-xl">
            <img
              src={product.image}
              alt={product.name}
              className="w-20 h-20 rounded-lg object-cover"
              width="80"
              height="80"
              loading="lazy"
              decoding="async"
            />
            <div className="flex-1">
              <p className="font-bold text-foreground">{product.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-sm line-through text-muted-foreground">
                  {product.price.toLocaleString()} IQD
                </span>
                <span className="text-lg font-bold text-green-600">مجاناً 🎉</span>
              </div>
            </div>
          </div>

          {/* Min cart condition */}
          {popupMinCartValue > 0 && (
            <div className={`text-center text-sm p-2 rounded-lg ${meetsMinCart ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400' : 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'}`}>
              {meetsMinCart 
                ? '✅ سلتك مؤهلة للعرض!'
                : `🛒 أضف منتجات بقيمة ${popupMinCartValue.toLocaleString()} IQD أو أكثر للحصول على الهدية`
              }
            </div>
          )}

          {/* CTA */}
          <Link to="/categories" onClick={handleClose}>
            <Button className="w-full rounded-xl gap-2 text-base h-12 mt-2">
              <ShoppingBag className="w-5 h-5" />
              تسوق الآن
            </Button>
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PromoPopup;
